/**
 * Copyright (c) 2023
 *
 * @file Provide an HTTP client adapter for application usage.
 * @author Justin Taylor <jtaylor@audioauthority.com>
 * @summary Abstract out http minutia for straightforward api calling.  Here we 
 *         can add special handling for transport related things like
 *         tab keep-alive, compression, etc.
 * 
 */

import axios, { AxiosRequestConfig, RawAxiosRequestHeaders, AxiosHeaders, AxiosResponse, AxiosError, AxiosRequestHeaders, AxiosHeaderValue } from "axios";
import MethodHeaders from "axios";
import { GenericModel } from "Models/genericModel";
import { ValidationError } from "../Core/error/ValidationError";
import _ from "lodash";

import { getServerURL as _getServerURL } from "Utilities/runtime.service";
import { AuthHTTP } from "Auth/Gateway/types";

export type SimpleAxiosHeaders = RawAxiosRequestHeaders | AxiosHeaders

//
// bootstrap
//

export const axiosInstance = axios.create({
  baseURL: _getServerURL(), //process.env?
  headers: { 'Content-Type': 'application/json'},

  // validateStatus: (status) => status < 400 || status === 422
  validateStatus: (status) => status < 400
});

const HTTP_ROOT_CLIENT: AxiosFn = axiosInstance.request;

// implement with axios interceptors
const browserTabKeepAlive = () => {};
const compressOutgoingPayload = () => {};

//
// types
//

// axios-specific signature
type AxiosFn = (config: AxiosRequestConfig) => Promise<AxiosResponse>;

// application uses this signature for http requests
export type AdapterFn = (url: string, requestOptions?: IOTRequestConfig) => Promise<AxiosResponse>

// create our internal client from any api (fetch, axios, XMLHttpRequest, etc)
export type AdapterCallFn = (client: AxiosFn) => AdapterFn // Promise<JSONResponse<GenericModel>>;

export interface IOTRequestConfig extends AxiosRequestConfig {
  body?: AuthHTTP.Body | null; // allows for usage of "body" member as needed
  headers?: keyof AxiosHeaderValue;
}

//
// adapter
//

/**
 * Delegates to axios, other adapters could delegate like this.
 *
 * @param      {NativeFn}  client  The client
 * @returns    {AdapterFn}  Client for sending http requests, adapted from any defined native client.
 */
const httpAdapter: AdapterCallFn = (client: AxiosFn): AdapterFn => (url, requestOptions = {}) => {
  let data;

  if (!_.isEmpty(requestOptions.body) && !_.isEmpty(requestOptions.data)) {
    console.warn(`Warning: request options contains both 'body' and 'data' properties.  Deferring to 'data'.`)
    data = requestOptions.data;
  }

  else if (!_.isEmpty(requestOptions.body)) {
    data = requestOptions.body;
    delete requestOptions.body;
  }

  const requestConfig: AxiosRequestConfig = {
    url,
    data: JSON.stringify(data),
    ...requestOptions,
  };

  // NOTE: invoking from here may cause `initiator` to be logged as from here
  //   instead of the invocation in application.  To change this, refactor to use `bind` instead.
  return client.call(null, requestConfig)
  .then(responsePipe)

}

const errorPipe = (error: AxiosError<any>) => {
  // console.group(`##### http.ts errorPipe #####`)
  // console.info(`##### error: `, error)
  // console.groupEnd()

  return Promise.reject(new Error(`HTTP ${error.response.status} ${error.response?.statusText},  error: '${error.message}'  (${error.code})`));
}

const responsePipe = (response: AxiosResponse) => {
  // console.group(`##### http.ts responsePipe #####`)
  // console.info(`##### response: `, response)
  // console.info(`##### response.data: `, response.data)
  // console.groupEnd()

  return response;
};

export default httpAdapter(HTTP_ROOT_CLIENT);

